<template>
  <v-app>
    <v-row justify="center">
      <v-col cols="12" md="6">
        <v-card class="app-card">
          <v-card-text>
            <v-row class="px-3">
              <v-col cols="12" class="text-center">
                <img :src="logotype" class="img-responsive mb-3" alt="Logotype" />
                <br/><br/>
                <h1 v-show="!loadingValidate" class="font-weight-bold primary--text mb-0">
                  Join {{ invitedBy }} on GreenGeeks
                </h1>
              </v-col>
              <v-col cols="12" class="text-center" v-show="loadingValidate">
                <v-progress-circular indeterminate size="64" color="primary" />
              </v-col>
              <v-col cols="12" v-show="!loadingValidate">
                <v-row>
                  <v-col cols="12" class="text-center">
                    <p class="mb-0">
                      You have been invited to join {{ invitedBy }}'s account.
                      Please fill out the form below to create your account.
                    </p>
                  </v-col>
                  <v-col cols="12">
                    <span class="subtitle-1 font-weight-bold">Account Information</span>
                    <v-divider></v-divider>
                  </v-col>
                </v-row>
                <v-form class="gg-inv-form" autocomplete="off">
                  <v-row>
                    <v-col cols="12" md="6" class="py-0">
                      <label>E-mail</label>
                      <v-text-field outlined
                                    dense
                                    disabled
                                    autocomplete="off"
                                    v-model.trim="model.email"
                                    data-vv-name="email"
                                    data-vv-as="Email"
                                    v-validate="'required|email'"
                                    :error-messages="errors.collect('email')"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                      <v-row>
                        <v-col class="py-0" cols="12">
                          <label>Phone Number</label>
                        </v-col>
                        <v-col class="py-0" cols="5">
                          <v-combobox outlined
                                      v-model="phone.dialCode"
                                      dense
                                      name="suffix"
                                      autocomplete="suffix"
                                      auto-select-first
                                      :items="dialCodes"
                          />
                        </v-col>
                        <v-col class="py-0" cols="7">
                          <v-text-field outlined
                                        dense
                                        v-model.trim="phone.phonenumber"
                                        data-vv-name="phonenumber"
                                        data-vv-as="Phone Number"
                                        v-validate="'required'"
                                        :error-messages="errors.collect('phonenumber')"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <label>First Name</label>
                      <v-text-field outlined
                                    dense
                                    v-model.trim="model.firstname"
                                    data-vv-name="firstname"
                                    data-vv-as="First Name"
                                    v-validate="'required'"
                                    autocomplete="new-password"
                                    :error-messages="errors.collect('firstname')"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <label>Last Name</label>
                      <v-text-field outlined
                                    dense
                                    v-model.trim="model.lastname"
                                    data-vv-name="lastname"
                                    data-vv-as="Last Name"
                                    v-validate="'required'"
                                    autocomplete="new-password"
                                    :error-messages="errors.collect('lastname')"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <label>Password</label>
                      <v-text-field outlined
                                    dense
                                    v-model="model.password"
                                    type="password"
                                    data-vv-name="password"
                                    ref="password"
                                    data-vv-as="Password"
                                    v-validate="'required|min:6'"
                                    autocomplete="new-password"
                                    :error-messages="errors.collect('password')"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <label>Confirm Password</label>
                      <v-text-field outlined
                                    dense
                                    v-model="model.passwordconfirm"
                                    type="password"
                                    data-vv-name="passwordconfirm"
                                    data-vv-as="Confirm Password"
                                    v-validate="'required|confirmed:password'"
                                    autocomplete="new-password"
                                    :error-messages="errors.collect('passwordconfirm')"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-row justify="center">
                        <v-checkbox v-model="agreement"
                                    data-vv-name="agreement"
                                    data-vv-as="Privacy Policy Agreement"
                                    v-validate="'required:true'"
                                    :error-messages="errors.collect('agreement')">
                          <template v-slot:label>
                            <div class="justify-center align-center text-center">
                              I confirm that I have read the
                              <a target="_blank"
                                 href="https://www.greengeeks.com/legal/tos"
                                 @click.stop>
                                Terms and Conditions
                              </a>
                              and the
                              <a target="_blank"
                                 href="https://www.greengeeks.com/legal/privacy"
                                 @click.stop>
                                Privacy Policy
                              </a>.
                            </div>
                          </template>
                        </v-checkbox>
                      </v-row>
                    </v-col>
                    <v-col cols="12" class="text-center">
                      <gg-btn color="primary" tile :loading="loadingRegister" @click="register">Continue & Get Access</gg-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { countries, states } from '@/helpers/countries'
import amapi from '@/api/amapi'

const dialCodes = countries.map(country => country.dial_code).sort((a, b) => a - b)

export default {
  name: 'PublicInvitation',
  data () {
    return {
      loadingValidate: true,
      loadingRegister: false,
      dialCodes,
      countries,
      states,
      invitedBy: '',
      ipIso: '',
      agreement: false,
      phone: {
        dialCode: dialCodes[0],
        phonenumber: '',
      },
      model: {
        hash: '',
        email: '',
        firstname: '',
        lastname: '',
        password: '',
        passwordconfirm: '',
      }
    }
  },
  computed: {
    ...mapGetters(['brand']),
    logotype () {
      return this.$vuetify.theme.dark ? this.brand.appLogo : this.brand.darkLogo
    },
    phoneNumber () {
      return this.phone.dialCode + '.' + this.phone.phonenumber
    }
  },
  methods: {
    ...mapActions(['reportError', 'reportSuccess']),
    async inviteValidate () {
      const hash = this.$route.params.hash
      try {
        const response = await amapi.makecall('invite_validate', { hash })
        this.model.hash = hash // set a valid hash
        this.loadingValidate = false
        this.model.email = response.email
        this.invitedBy = response.invited_by
        this.ipIso = response.ip_iso
      } catch (e) {
        this.reportError(e)
        this.loadingValidate = false
        this.$router.push({ name: 'login' })
      }
      const validCountry = this.countries.find(country => country.code == this.ipIso)
      if (validCountry) {
        this.phone.dialCode = validCountry.dial_code
      }
    },
    async register () {
      const valid = await this.$validator.validateAll()
      if (!valid) return
      this.loadingRegister = true
      try {
        const model = this.model
        model.phonenumber = this.phoneNumber

        const response = await amapi.makecall('invite_register', { ...model })
        this.reportSuccess(response.resultmsg)
        this.$router.push({ name: 'login' })
      } catch (e) {
        this.reportError(e)
        this.loadingRegister = false
      }
      this.loadingRegister = false
    }
  },
  mounted () {
    const dict = {
      custom: {
        agreement: {
          required: 'You must confirm you have read our Terms of Service and Privacy Policy.'
        }
      }
    }
    this.$validator.localize('en', dict)
    this.$nextTick(() => {
      this.inviteValidate()
    })
  }
}
</script>

<style scoped>
  .gg-inv-form label {
    font-weight: 700;
  }
</style>
